import { supabase } from '../supabase';
import { Task, TaskPriority, TaskStatus } from '../../../types';
import { logger } from '../../../utils/logger';

export const taskMutations = {
  async createTask(taskData: {
    title: string;
    description: string;
    assigned_to: string;
    assigned_by: string;
    department: string;
    priority: TaskPriority;
    due_date: Date;
  }) {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          ...taskData,
          status: 'PENDING',
          acknowledged: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select(`
          *,
          subtasks (*),
          comments:task_comments (
            *,
            user:profiles(name, avatar)
          ),
          assigned_to:profiles!tasks_assigned_to_fkey(name),
          assigned_by:profiles!tasks_assigned_by_fkey(name)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Failed to create task:', error);
      throw error;
    }
  },

  async updateTask(taskId: string, updates: Partial<Task>) {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId)
        .select(`
          *,
          subtasks (*),
          comments:task_comments (
            *,
            user:profiles(name, avatar)
          )
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Failed to update task:', error);
      throw error;
    }
  },

  async updateTaskStatus(taskId: string, status: TaskStatus) {
    try {
      logger.info('Updating task status:', { taskId, status });
      
      const { data, error } = await supabase
        .from('tasks')
        .update({
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId)
        .select(`
          *,
          subtasks (*),
          comments:task_comments (
            *,
            user:profiles(name, avatar)
          )
        `)
        .single();

      if (error) throw error;
      
      // If completing task, also complete all subtasks
      if (status === 'COMPLETED') {
        const { error: subtaskError } = await supabase
          .from('subtasks')
          .update({
            status: 'COMPLETED',
            completed_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('task_id', taskId)
          .not('status', 'eq', 'COMPLETED');

        if (subtaskError) {
          logger.error('Error completing subtasks:', subtaskError);
        }
      }

      return data;
    } catch (error) {
      logger.error('Failed to update task status:', error);
      throw error;
    }
  },

  async acknowledgeTask(taskId: string) {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .update({
          acknowledged: true,
          acknowledged_at: new Date().toISOString(),
          status: 'IN_PROGRESS',
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId)
        .select(`
          *,
          subtasks (*),
          comments:task_comments (
            *,
            user:profiles(name, avatar)
          )
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Failed to acknowledge task:', error);
      throw error;
    }
  },

  async addTaskComment(taskId: string, userId: string, content: string) {
    try {
      const { data, error } = await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          user_id: userId,
          content,
          created_at: new Date().toISOString()
        })
        .select(`
          *,
          user:profiles!task_comments_user_id_fkey (
            id,
            name,
            avatar,
            department,
            role
          )
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Failed to add task comment:', error);
      throw error;
    }
  },

  async createSubtask(taskId: string, subtaskData: {
    title: string;
    description?: string;
    due_date?: Date;
  }) {
    try {
      const { data, error } = await supabase
        .from('subtasks')
        .insert({
          task_id: taskId,
          ...subtaskData,
          status: 'PENDING',
          acknowledged: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Failed to create subtask:', error);
      throw error;
    }
  },

  async acknowledgeSubtask(subtaskId: string) {
    try {
      const { data, error } = await supabase
        .from('subtasks')
        .update({
          acknowledged: true,
          acknowledged_at: new Date().toISOString(),
          status: 'IN_PROGRESS',
          updated_at: new Date().toISOString()
        })
        .eq('id', subtaskId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Failed to acknowledge subtask:', error);
      throw error;
    }
  },

  async completeSubtask(subtaskId: string) {
    try {
      const { data: subtask, error: subtaskError } = await supabase
        .from('subtasks')
        .update({
          status: 'COMPLETED',
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', subtaskId)
        .select('task_id')
        .single();

      if (subtaskError) throw subtaskError;

      // Check if all subtasks are completed
      const { data: subtasks, error: checkError } = await supabase
        .from('subtasks')
        .select('status')
        .eq('task_id', subtask.task_id);

      if (checkError) throw checkError;

      const allCompleted = subtasks.every(st => st.status === 'COMPLETED');

      // Update parent task status if all subtasks are completed
      if (allCompleted) {
        const { error: taskError } = await supabase
          .from('tasks')
          .update({
            status: 'COMPLETED',
            updated_at: new Date().toISOString()
          })
          .eq('id', subtask.task_id);

        if (taskError) throw taskError;
      }

      return subtask;
    } catch (error) {
      logger.error('Failed to complete subtask:', error);
      throw error;
    }
  }
};